<template>
  <div class="workspaces" :key="route.path">
    <div class="settings-container">
      <div>
        <ui-info
          v-if="!store.user?.workspacesHelpClosed"
          class="mb-16"
          :closeHandle="closeBanner"
          :html="
            t(
              'By default there are 2 default workspaces: Public and Master.<br> Public is a common area where campaign elements are shared with other members.<br> Master is a default private workspace. Only Owner has the access to this WS and invites other members to Master.<br> As Account Owner you can create unlimited amount of Private WS to separate the required campaigns and elements and delegate the management to your team members.<br>Moving campaigns and elements between WS is not possible in current Workspaces version.',
            )
          "
        />
        <div class="main">
          <div class="left">
            <div class="left-head">
              <div class="search">
                <ui-input
                  v-model="workspacesSearch"
                  prepend-inner-icon="fas fa-search"
                  type="text"
                  :placeholder="t('Search')"
                  :hide-details="1"
                />
              </div>
              <ui-button
                :text="t('Add Workspace')"
                icon="fas fa-plus"
                button-style="accent"
                @click="openCreateWorkspaceDialog(null)"
              />
            </div>
            <ui-select
              v-if="searchedWorkspaces.length !== 0"
              class="v-input--no-label left-select-workspace"
              v-model="activeWorkspace"
              :items="searchedWorkspaces"
              item-text="name"
              return-object
              attach
            />
            <div class="list" v-if="searchedWorkspaces.length !== 0">
              <button
                :key="workspace.id"
                :class="{
                  'list-item': true,
                  'list-item--active': activeWorkspace.id === workspace.id,
                }"
                type="button"
                @click="setActiveWorkspace(workspace.id)"
                v-for="workspace in searchedWorkspaces"
              >
                <div class="list-item-name align-center">
                  <div class="list-item-name-text">
                    {{ workspace.name }}
                  </div>
                  <ui-icon
                    v-if="workspace.type === 'public'"
                    size="12"
                    :color="$colors.green"
                    icon="fas fa-lock"
                    class="ml-4"
                  />
                </div>
                <div class="list-item-info">
                  {{ workspace.membersCount }}
                  <ui-icon
                    size="12"
                    :color="$colors.gray40"
                    icon="fas fa-user"
                    class="mr-8 ml-4"
                  />
                  {{ workspace.itemsCount }}
                  <ui-icon
                    size="12"
                    :color="$colors.gray40"
                    icon="fas fa-bullseye-arrow"
                    class="mr-16 ml-4"
                  />
                </div>
                <div class="list-item-actions">
                  <button
                    class="list-button"
                    @click="
                      openCreateWorkspaceDialog({
                        name: workspace.name,
                        id: workspace.id,
                        created: workspace.created,
                        updated: workspace.updated,
                        updatedBy: workspace.updatedBy,
                        trackingDomainIds: workspace.trackingDomainIds,
                      })
                    "
                  >
                    <ui-icon
                      size="12"
                      :color="$colors.green"
                      icon="fas fa-pen"
                      class="mr-16"
                    />
                  </button>
                  <button
                    v-if="isAllowedDelete(workspace)"
                    class="list-button"
                    @click="removeWorkspaceConfirmCall(workspace)"
                  >
                    <ui-icon
                      size="12"
                      :color="$colors.red"
                      icon="fas fa-trash-alt"
                      class="mr-16"
                    />
                  </button>
                </div>
              </button>
            </div>
            <div v-else>
              <ui-not-found class="justify-content-start pb-16" />
            </div>
          </div>

          <div class="right settings-block" :key="activeWorkspace?.id">
            <div v-if="activeWorkspace?.id">
              <div class="title-block settings-block__title">
                <div class="title">{{ activeWorkspace.name }}</div>
                <div class="panel-title-right">
                  <a
                    :href="`https://docs.bemob.com/${locale}/workspaces`"
                    target="_blank"
                    class="panel-title-link"
                  >
                    <ui-tooltip-icon
                      preset="help"
                      :text="t('Find out more about workspace settings in our guides')"
                    />
                    <span class="read-guide ml-6">
                      {{ t('Read Guide') }}
                    </span>
                  </a>
                  <div class="d-flex">
                    <ui-button
                      :text="t('Edit')"
                      icon="fas fa-pen"
                      @click="openCreateWorkspaceDialog(activeWorkspace)"
                    />
                    <ui-button
                      :text="t('Delete')"
                      :disabled="!isAllowedDelete(activeWorkspace)"
                      icon="fas fa-trash-alt"
                      @click="removeWorkspaceConfirmCall(activeWorkspace)"
                    />
                    <ui-button
                      :text="t('Add users')"
                      icon="fas fa-plus"
                      button-style="outline-accent"
                      menu-content-class="checkbox-menu"
                      :menu-props="{ location: 'bottom end' }"
                      v-model:isMenuOpen="isAddMemberMenuOpened"
                    >
                      <template #menu>
                        <div class="checkbox-menu__content">
                          <div class="checkbox-menu__top">
                            <ui-checkbox
                              v-model="addMemberMenuIsSelectedAll"
                              class="pt-0 mt-4 mb-8 checkbox"
                              :label="t('All users')"
                            />
                            <div class="checkbox-menu__search">
                              <ui-input
                                v-model="membersSearch"
                                prepend-inner-icon="fas fa-search"
                                type="text"
                                :placeholder="t('Search')"
                                :hide-details="1"
                              />
                            </div>
                            <template v-if="searchedMembers.length > 0">
                              <ui-checkbox
                                v-for="member in searchedMembers"
                                class="checkbox-menu__list-item checkbox"
                                :key="member.id"
                                :true-value="member"
                                v-model="addMemberMenuSelected"
                                :label="member.fullName"
                              />
                            </template>
                            <ui-not-found v-else />
                          </div>
                          <div class="checkbox-menu__bottom">
                            <ui-button
                              :text="t('Cancel')"
                              icon="fas fa-times"
                              :disabled="loading"
                              @click="toggleAddMemberMenu"
                            />

                            <ui-button
                              :text="t('Save')"
                              icon="fas fa-check"
                              button-style="accent"
                              :loading="addMemberMenuLoading"
                              @click="saveAddMemberMenu"
                            />
                          </div>
                        </div>
                      </template>
                    </ui-button>
                  </div>
                </div>
              </div>
              <div class="details">
                <div class="details-left">
                  <div class="details-left-title">
                    {{ t('Tracking Domains') }}
                  </div>
                  <ui-tooltip-icon
                    preset="help"
                    class="mb-2 ml-4 mr-4"
                    :text="t('Tracking domains assigned to the specific workspace')"
                  />
                  <div
                    v-if="getTrackingDomainsForActiveWorkspace.length !== 0"
                    class="onePlus-wrp"
                  >
                    <div class="onePlus-text">
                      {{ getTrackingDomainsForActiveWorkspace[0] }}
                    </div>
                    <v-tooltip
                      v-if="getTrackingDomainsForActiveWorkspace.length > 1"
                      :color="$colors.white"
                      content-class="onePlus-tooltip onePlus-tooltip__workspaces"
                      class="ml-8"
                      location="top"
                    >
                      <template #activator="{ props }">
                        <div class="onePlus-text onePlus-text--count ml-8" v-bind="props">
                          {{ `+${getTrackingDomainsForActiveWorkspace.length - 1}` }}
                        </div>
                      </template>
                      <div
                        v-for="trackingDomain in getTrackingDomainsForActiveWorkspace.slice(
                          1,
                        )"
                        class="onePlus-text"
                      >
                        {{ trackingDomain }}
                      </div>
                    </v-tooltip>
                  </div>
                  <div v-else class="onePlus-text">
                    {{ t('Account default') }}
                  </div>
                </div>
                <button
                  type="button"
                  @click="openItemsInWorkspaceDialog(activeWorkspace)"
                  class="details-right"
                >
                  {{ activeWorkspace.itemsCount }}
                  {{
                    activeWorkspace.itemsCount === 1
                      ? t('Item in This Workspace')
                      : t('Items in This Workspace')
                  }}
                  <ui-icon
                    size="13"
                    :color="$colors.green"
                    icon="fas fa-eye"
                    class="ml-4"
                  />
                </button>
              </div>
              <ui-table
                class="users-table"
                :loading="workspacesOperationsLoading"
                :headers="headers"
                :items="tableRows"
                :virtualize="false"
                :rows-per-page-count="10"
                :use-search="false"
              >
                <template #item.name="{ item }">
                  <div class="main-table__name">
                    <div class="main-table__name-text">
                      {{ item.name }}
                    </div>
                    <ui-icon
                      v-if="item.status === 'blocked'"
                      class="ml-4"
                      size="12"
                      :color="$colors.red"
                      icon="fas fa-lock"
                    />
                  </div>
                </template>
                <template #item.email="{ item }"> {{ item.email }}</template>
                <template #item.role="{ item }"> {{ t(item.role) }}</template>
                <template #item.actions="{ item }">
                  <div class="table-buttons">
                    <button
                      v-if="activeWorkspace.type !== 'public'"
                      type="button"
                      class="table-permission"
                      @click="
                        openEditAccessPermissionsDialog({
                          user: allMembersNormalized[item.id],
                          activeWorkspace,
                        })
                      "
                    >
                      <ui-icon size="13" class="mr-4" icon="fas fa-key" />
                      {{ t('Permissions') }}
                    </button>
                    <v-menu
                      content-class="table-menu"
                      :transition="false"
                      location="bottom end"
                      :offset="[5, 19]"
                    >
                      <template #activator="{ props }">
                        <ui-icon
                          size="13"
                          class="ml-16 mr-16 pointer"
                          :color="$colors.green"
                          v-bind="props"
                          icon="fas fa-ellipsis-h"
                        />
                      </template>
                      <div class="table-submenu">
                        <button
                          type="button"
                          @click="openCreateMemberDialog(item.id)"
                          class="table-submenu-button"
                        >
                          {{ t('Edit') }}
                          <ui-icon size="13" class="ml-4" icon="fas fa-pen" />
                        </button>
                        <button
                          type="button"
                          v-if="activeWorkspace.type !== 'public'"
                          @click="removeUserFromWorkspaceConfirmCall(item.id)"
                          class="table-submenu-button table-submenu-button--remove"
                        >
                          {{ t('Remove From Workspace') }}
                          <ui-icon
                            size="13"
                            :color="$colors.red"
                            class="ml-4"
                            icon="fas fa-unlink"
                          />
                        </button>
                      </div>
                    </v-menu>
                  </div>
                </template>
              </ui-table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import _ from 'lodash';
import { useColors, useI18n } from '@/composables';
import { useStore } from '@/store';
import { $emitter } from '@/services/emitter';
import {
  deleteWorkspaceApiRequest,
  putUserWorkspaceApiRequest,
  type UserWorkspaceWritePayload,
} from '@/services/api/workspaces-api';
import { refreshUserProfileFromApi } from '@/services/api/api-services/load-profile';
import { patchUserApiRequest } from '@/services/api/user-api';
import type { User, UserWorkspace } from '@/services/models';

type WorkspaceLike = {
  id: string;
  name: string;
  type: string;
  created?: string;
  updated?: string;
  updatedBy?: string;
  trackingDomainIds: string[];
  memberIds: string[];
  itemsCount: number;
  membersCount: number;
  deletable?: number;
};

type MemberLike = {
  id: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  login: string;
  role: string;
  status?: string;
};

const store = useStore();
const route = useRoute();
const { t, locale } = useI18n();
const $colors = useColors();
const props = withDefaults(
  defineProps<{
    workspacesData?: UserWorkspace[];
    workspacesDataNormalized?: Record<string, UserWorkspace>;
    privateWorkspacesData?: UserWorkspace[];
    privateWorkspacesDataNormalized?: Record<string, UserWorkspace>;
    refreshWorkspaces?: () => Promise<void>;
    applyWorkspace?: (workspace: UserWorkspace) => void;
    membersData?: unknown[];
    membersDataNormalized?: Record<string, unknown>;
    membersLoading?: boolean;
    refreshMembers?: () => Promise<void>;
  }>(),
  {
    workspacesData: () => [],
    workspacesDataNormalized: () => ({}),
    privateWorkspacesData: () => [],
    privateWorkspacesDataNormalized: () => ({}),
    membersData: () => [],
    membersDataNormalized: () => ({}),
    membersLoading: false,
  },
);

const workspacesOperationsLoading = ref(false);

const loading = ref(false);
const addMemberMenuLoading = ref(false);
const activeWorkspace = ref<WorkspaceLike>({} as WorkspaceLike);
const allWorkspaces = ref<WorkspaceLike[]>([]);
const allWorkspacesNormalized = ref<Record<string, WorkspaceLike>>({});
const workspacesSearch = ref('');
const allMembers = ref<MemberLike[]>([]);
const allMembersNormalized = ref<Record<string, MemberLike>>({});
const membersSearch = ref('');
const isAddMemberMenuOpened = ref(false);
const addMemberMenuSelected = ref<MemberLike[]>([]);

const searchedWorkspaces = computed(() => {
  const searchValue = workspacesSearch.value.trim().toLowerCase();
  if (!searchValue) {
    return allWorkspaces.value;
  }
  return allWorkspaces.value.filter((workspace: WorkspaceLike) => {
    return workspace.name && workspace.name.toLowerCase().includes(searchValue);
  });
});

const searchedMembers = computed(() => {
  const searchValue = membersSearch.value.trim().toLowerCase();
  if (!searchValue) {
    return allMembers.value;
  }
  return allMembers.value.filter((member: MemberLike) => {
    return member.fullName && member.fullName.toLowerCase().includes(searchValue);
  });
});

const getMembersForActiveWorkspace = computed(() => {
  const memberIds = activeWorkspace.value?.memberIds ?? [];
  if (memberIds.length === 0) {
    return [];
  }
  return allMembers.value.filter((member: MemberLike) => memberIds.includes(member.id));
});

const tableRows = computed(() => {
  return getMembersForActiveWorkspace.value.map((row: MemberLike) => {
    const { firstName, lastName, login, role, id, status } = row;
    return {
      name: `${firstName || ''}${lastName ? ` ${lastName}` : ''}`,
      email: login,
      role,
      status,
      id,
    };
  });
});

const getTrackingDomainsForActiveWorkspace = computed(() => {
  const trackingDomainIds = activeWorkspace.value?.trackingDomainIds;
  if (!trackingDomainIds) {
    return [];
  }
  return trackingDomainIds
    .map((domainId: string) =>
      _.get(store.trackingDomainsObject, [domainId, 'domain'], null),
    )
    .filter((item: unknown) => !!item);
});

const headers = computed(() => [
  { title: t('Name'), value: 'name', sortable: true },
  { title: t('Email'), value: 'email', sortable: true },
  { title: t('Role'), value: 'role', sortable: true },
  { title: '', value: 'actions', sortable: false },
]);

const addMemberMenuIsSelectedAll = computed({
  get: () => addMemberMenuSelected.value.length === allMembers.value.length,
  set: (isSelectedAll: boolean) => {
    if (isSelectedAll) {
      addMemberMenuSelected.value = [...allMembers.value];
      return;
    }
    addMemberMenuSelected.value = [];
  },
});

const setActiveWorkspace = (workspaceId?: string) => {
  if (workspaceId) {
    activeWorkspace.value =
      allWorkspacesNormalized.value[workspaceId] ?? ({} as WorkspaceLike);
    return;
  }

  const activeWorkspaceId = activeWorkspace.value?.id;
  if (activeWorkspaceId && allWorkspacesNormalized.value[activeWorkspaceId]) {
    activeWorkspace.value = allWorkspacesNormalized.value[activeWorkspaceId];
    return;
  }

  activeWorkspace.value =
    (allWorkspaces.value[0] as WorkspaceLike | undefined) ?? ({} as WorkspaceLike);
};

const toggleAddMemberMenu = () => {
  isAddMemberMenuOpened.value = !isAddMemberMenuOpened.value;
};

const applyWorkspace = (workspace: UserWorkspace) => {
  props.applyWorkspace?.(workspace);
  const normalized = {
    ...allWorkspacesNormalized.value,
    [workspace.id]: workspace as WorkspaceLike,
  };
  allWorkspacesNormalized.value = normalized;
  allWorkspaces.value = _.orderBy(Object.values(normalized), ['deletable', 'name'], [
    'asc',
    'asc',
  ]);
};

const openCreateWorkspaceDialog = (workspace?: WorkspaceLike) => {
  $emitter.emit('open-dialog', {
    name: 'CreateWorkspaceDialog',
    data: {
      workspace,
      setActiveWorkspace,
      applyWorkspace,
    },
  });
};

const openCreateMemberDialog = (memberId?: string) => {
  if (!memberId) return;
  const user = allMembersNormalized.value[memberId];
  if (!user) return;
  $emitter.emit('open-dialog', {
    name: 'CreateMember',
    data: {
      user,
      privateWorkspacesData: props.privateWorkspacesData,
      privateWorkspacesDataNormalized: props.privateWorkspacesDataNormalized,
      refreshWorkspaces: props.refreshWorkspaces,
      refreshMembers: props.refreshMembers,
    },
  });
};

const openEditAccessPermissionsDialog = (data: unknown) => {
  $emitter.emit('open-dialog', { name: 'EditAccessPermissions', data });
};

const openItemsInWorkspaceDialog = (workspace: WorkspaceLike) => {
  $emitter.emit('open-dialog', {
    name: 'ItemsInWorkspace',
    data: { activeWorkspace: workspace },
  });
};

const isAllowedDelete = (workspace: WorkspaceLike) => {
  const itemsCount = workspace.itemsCount ?? 0;
  const trackingDomainIds = workspace.trackingDomainIds ?? [];
  const memberIds = workspace.memberIds ?? [];
  const { name } = workspace;
  if (name === 'Master' || name === 'Public') return false;
  return memberIds.length === 0 && trackingDomainIds.length === 0 && itemsCount === 0;
};

const removeWorkspaceConfirmCall = (workspace: WorkspaceLike) => {
  $emitter.emit('open-dialog', {
    name: 'ConfirmDialog',
    data: {
      message: t('Selected workspace will be deleted if you press OK.'),
      onConfirm: () => {
        workspacesOperationsLoading.value = true;
        void deleteWorkspaceApiRequest(workspace.id)
          .then(() => refreshUserProfileFromApi())
          .then(() => props.refreshWorkspaces?.())
          .then(() => {
            $emitter.emit('toast-success', t('Workspace successfully deleted'));
          })
          .catch((error: unknown) => {
            const errors = (
              error as { response?: { data?: { errors?: Record<string, string> } } }
            )?.response?.data?.errors;
            if (!errors) return;
            Object.values(errors).forEach((errorMessage: string) => {
              $emitter.emit('toast-error', t(errorMessage));
            });
          })
          .finally(() => {
            workspacesOperationsLoading.value = false;
          });
      },
    },
  });
};

const updateWorkspace = (payload: Record<string, unknown>) => {
  if (!activeWorkspace.value?.id) return Promise.resolve();

  addMemberMenuLoading.value = true;
  workspacesOperationsLoading.value = true;
  return putUserWorkspaceApiRequest({
    ...(activeWorkspace.value as Record<string, unknown>),
    ...payload,
    id: activeWorkspace.value.id,
  } as UserWorkspaceWritePayload)
    .then(() => refreshUserProfileFromApi())
    .then(() => props.refreshWorkspaces?.())
    .then(() => props.refreshMembers?.())
    .then(() => {
      $emitter.emit('toast-success', t('Workspace successfully updated'));
    })
    .finally(() => {
      addMemberMenuLoading.value = false;
      workspacesOperationsLoading.value = false;
    });
};

const removeUserFromWorkspaceConfirmCall = (memberId: string) => {
  $emitter.emit('open-dialog', {
    name: 'ConfirmDialog',
    data: {
      message: t('Do you want to remove user from workspace?'),
      onConfirm: () => {
        const memberIds = (activeWorkspace.value.memberIds ?? []).filter(
          (currentMemberId: string) => currentMemberId !== memberId,
        );
        void updateWorkspace({ memberIds });
      },
    },
  });
};

const saveAddMemberMenu = () => {
  const memberIds = addMemberMenuSelected.value.map((member: MemberLike) => member.id);
  void updateWorkspace({ memberIds }).then(() => {
    toggleAddMemberMenu();
  });
};

const closeBanner = async () => {
  const response = await patchUserApiRequest({ workspacesHelpClosed: true });
  store.mergeUser(response.data.payload as User);
};

watch(
  activeWorkspace,
  value => {
    if (!value) {
      activeWorkspace.value = {} as WorkspaceLike;
      return;
    }

    if (!value.id) {
      addMemberMenuSelected.value = [];
      membersSearch.value = '';
      return;
    }

    const selectedMembers: MemberLike[] = [];
    const memberIds = value.memberIds ?? [];
    memberIds.forEach((memberId: string) => {
      const member = allMembersNormalized.value[memberId];
      if (member) {
        selectedMembers.push(member);
      }
    });
    addMemberMenuSelected.value = selectedMembers;
    membersSearch.value = '';
  },
  { deep: true },
);

watch(
  () => [props.workspacesData, props.workspacesDataNormalized],
  () => {
    allWorkspaces.value = [...(props.workspacesData as WorkspaceLike[])];
    allWorkspacesNormalized.value = {
      ...(props.workspacesDataNormalized as Record<string, WorkspaceLike>),
    };
    setActiveWorkspace();
  },
  { deep: true, immediate: true },
);

watch(
  () => [props.membersData, props.membersDataNormalized],
  () => {
    allMembers.value = [...(props.membersData as MemberLike[])];
    allMembersNormalized.value = {
      ...(props.membersDataNormalized as Record<string, MemberLike>),
    };
  },
  { deep: true, immediate: true },
);
</script>

<style lang="scss" scoped>
@import '@/styles/pages/settings/multi-user-access';
.users-table {
  :deep(.ui-table__inner) {
    table-layout: fixed;
    min-width: 550px;
  }

  :deep(.table-header > .table-cell),
  :deep(.table-body .table-row > .table-cell) {
    text-overflow: ellipsis;
    overflow: hidden;
    text-align: left;
    white-space: nowrap;

    &:nth-child(1) {
      width: 32% !important;
      padding-right: 5px !important;
    }
    &:nth-child(2) {
      width: 32% !important;
      padding-right: 5px !important;
    }
    &:nth-child(3) {
      width: 60px !important;
      padding-right: 5px !important;
    }
    &:nth-child(4) {
      padding-right: 0 !important;
      width: 150px !important;

      &:lang(es) {
        width: 180px !important;
      }
    }
  }
}

.main {
  display: flex;
}
.left {
  width: 30%;
  padding-right: 32px;
}
.left-head {
  display: flex;
  padding-bottom: 12px;
}
.right {
  width: 70%;
}

.search {
  position: relative;
  flex-grow: 1;
  margin-right: 8px;
}

.search-icon {
  position: absolute;
  top: 9px;
  left: 8px;
}

.search-input {
  border: solid 1px var(--color-gray20);
  border-radius: 6px;
  font-size: 12px;
  height: 32px;
  width: 100%;
  padding: 0 8px 0 24px;
}

.add-workspace {
  height: 32px !important;
  margin: 0;
}

.list-item {
  width: 100%;
  cursor: pointer;
  height: 40px;
  box-shadow: 0 2px 4px 0 var(--color-black10);
  margin-bottom: 8px;
  padding: 0 0 0 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid var(--color-white);
  background: var(--color-white);
  &--active {
    border: 1px solid var(--color-green);
  }
  &:hover {
    border: 1px solid var(--color-green);
    .list-item-info {
      display: none;
    }
    .list-item-actions {
      display: flex;
    }
  }
}

.list-button {
  display: flex;
  align-items: center;
}

.list-item-name {
  max-width: 100%;
  margin-right: 16px;
  display: flex;
  overflow: hidden;
}

.list-item-name-text {
  max-width: 100%;
  overflow: hidden;
  font-weight: bold;
  font-size: 14px;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.list-item-actions {
  display: none;
}

.list-item-info {
  display: flex;
}

.list-item-info,
.list-item-actions {
  justify-content: space-between;
  align-items: center;
}

.panel-button-add {
  border: 1px solid var(--color-green) !important;
}
.title {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  flex: 1;
  font-size: 18px;
  font-weight: bold;
}
.details {
  display: flex;
  white-space: nowrap;
  justify-content: flex-end;
  flex-wrap: wrap;
}
.details-left {
  min-height: 24px;
  display: flex;
  align-items: center;
  margin-right: auto;
  margin-bottom: 8px;
  max-width: 100%;
}
.details-right {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  margin-left: 8px;
  padding: 6px 8px;
  background: var(--color-gray5);
  border-radius: 4px;
  font-size: 12px;
  line-height: 12px;
}
.details-left-title {
  font-weight: bold;
}

.table-submenu {
  background: var(--color-white);
  border-radius: 8px !important;
  display: flex;
  flex-direction: column;
}

.table-buttons {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.table-submenu-button {
  display: flex;
  width: 188px;
  justify-content: space-between;
  padding: 8px;
  align-items: center;
  border-bottom: 1px solid var(--color-green20);
}

.table-submenu-button--remove {
  border-bottom: none;
  color: var(--color-red);
}

.table-submenu-activator {
}

.table-submenu-inside {
  background: var(--color-white);
}

.table-permission {
  display: flex;
  align-items: center;
}

.list {
  height: 500px;
  overflow-y: auto;
}

:deep(.left-select-workspace) {
  display: none;
  .v-select__selection {
    overflow: hidden;
    white-space: nowrap;
    width: calc(100% - 30px);
    max-width: initial;
  }
}

.title-block {
  display: flex;
  justify-content: space-between;
}

@media all and (max-width: 900px) {
  .left-select-workspace {
    display: block;
  }
  .list {
    display: none;
  }
  .left {
    width: 100%;
    padding-right: 0;
  }

  .right {
    width: 100%;
  }
  .main {
    flex-direction: column;
  }
}
@media all and (max-width: 750px) {
  .title {
    width: 100%;
    margin-bottom: 8px;
  }
  .panel-title-right {
    width: 100%;
    justify-content: flex-end;
    display: flex;
    align-items: center;
  }
  .title-block {
    flex-direction: column;
  }
}

@media all and (max-width: 480px) {
  .details-left {
    flex-wrap: wrap;
  }
}

:deep(.main-table) {
  .v-table__overflow {
    padding-top: 8px !important;
  }
  table.v-datatable {
    table-layout: fixed;

    thead tr th {
      .v-icon {
        margin: 0 6px !important;
        position: initial;
        bottom: initial !important;
      }
    }
  }
}

.checkbox {
  display: flex !important;
}

.main-table__name {
  display: flex;
}

.main-table__name-text {
  text-overflow: ellipsis;
  overflow: hidden;
}

.onePlus-tooltip__workspaces {
  max-width: 700px !important;
  max-height: 90vh;
  overflow-y: auto;
}
</style>
